import { sortBy } from 'lodash-es';
import { makeAutoObservable, runInAction } from 'mobx';

import {
  CatalogRequests,
  type CategoryListAPI,
  LandingPage,
} from '~/api/Catalog';
import { CategoryDisplayType } from '~/components/CategoryBlocks/CategoryBlock';
import { mainStore } from '~/stores';
import { Category } from '~/stores/CategoriesStore/Category';
import { Task } from '~/stores/shared/Task';
import { copyWith } from '~/utils/copy';
import { PreloadCache } from '~/utils/preloadCache';

export class ShopCategories {
  public readonly request = new Task(this.loadCategories.bind(this));

  private data: Category[] = [];

  constructor(serverWarehouse?: string, serverLang?: string) {
    if (serverWarehouse) {
      this.init(serverWarehouse, serverLang);
    }

    makeAutoObservable(this);
  }

  public static preloadedData = new PreloadCache<CategoryListAPI[]>({
    max: 500,
    ttlMs: 60_000,
  });

  public static async preload(warehouse: string, serverLang?: string) {
    const data = await this.request(warehouse, serverLang);

    this.preloadedData.set(this.getCategoriesId(warehouse, serverLang), data);

    return data;
  }

  public static removePreloadedData(id: string) {
    this.preloadedData.delete(id);
  }

  public static getCategoriesId(warehouse: string, serverLang?: string) {
    return `${warehouse}.${serverLang}`;
  }

  private static request(warehouse: string, serverLang?: string) {
    return CatalogRequests.getCategories(
      {
        warehouseCode: warehouse,
        landingPage: [LandingPage.SHOP],
        depth: 3,
      },
      serverLang,
    ).then(({ data }) => data);
  }

  public get list(): Readonly<Category[]> {
    return this.data;
  }

  public get mainCategories() {
    return this.list.filter(
      (category) =>
        category.subcategory &&
        category.subcategory.length > 0 &&
        !category.showInSupermenu,
    );
  }

  get startShoppingCategory() {
    const category = this.data.find(({ name }) =>
      name.toLowerCase().includes('start shopping'),
    );

    if (!category) {
      return null;
    }

    return copyWith(category, {
      externalLink: '/shop',
      displayType: CategoryDisplayType.CATEGORIES_GRID_ONE_UNMASKED,
    });
  }

  private async loadCategories(warehouse: string) {
    try {
      const data = await ShopCategories.request(warehouse);

      runInAction(() => {
        this.data = sortBy(data, 'sorting')
          .filter(Category.validate)
          .map((data) => new Category(data));
      });

      return data;
    } catch (error) {
      return mainStore.errorHandler(error as never, 'requestCategories');
    }
  }

  private init(serverWarehouse: string, serverLang?: string) {
    const isSSR = typeof window === 'undefined';
    const ssrDegraded = !isSSR && window.__INITIAL_STATE__?.ssrDegraded;
    const preloadedData = ssrDegraded
      ? null
      : (!isSSR ? window.__INITIAL_STATE__?.shopCategories : null) ||
        ShopCategories.preloadedData.get(
          ShopCategories.getCategoriesId(serverWarehouse, serverLang),
        );

    if (!preloadedData || preloadedData.length === 0) {
      // Degraded SSR or CSR without data — fetch fresh categories on mount.
      if (!isSSR) {
        this.request.run(serverWarehouse);
      }
      return;
    }

    this.data = sortBy(preloadedData, 'sorting')
      .filter(Category.validate)
      .map((data) => new Category(data));
  }
}
